import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Clock, Download, Calendar, MessageSquare, Send, BookOpen, Play } from 'lucide-react';
import DOMPurify from 'dompurify';
import Navbar from '../components/Navbar.jsx';
import Footer from '../components/Footer.jsx';
import Seo from '../components/Seo.jsx';
import { supabase } from '../lib/supabase';
import PdfViewerModal from '../components/PdfViewerModal.jsx';

function textSnippet(html = '', words = 30) {
  return html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim().split(' ').slice(0, words).join(' ');
}

// Accepts youtu.be/ID, youtube.com/watch?v=ID, youtube.com/embed/ID, youtube.com/shorts/ID
function getYouTubeId(url) {
  if (!url) return null;
  const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{11})/);
  return match ? match[1] : null;
}

// Matches full TipTap output (StarterKit + rich-text extensions).
// DOMPurify still blocks javascript:/data: URIs and dangerous CSS automatically.
const PURIFY_CONFIG = {
  ALLOWED_TAGS: [
    // Block elements
    'p', 'br', 'strong', 'em', 'u', 's', 'code', 'pre',
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'ul', 'ol', 'li', 'blockquote', 'hr',
    // Rich-text extensions
    'a',    // Link
    'img',  // CustomImage
    'span', // TextStyle / color
    'mark', // Highlight
  ],
  ALLOWED_ATTR: [
    'href', 'target', 'rel',  // <a>
    'src', 'alt', 'title',    // <img>
    'style',                  // alignment, color, image width — DOMPurify sanitises CSS
  ],
  ALLOW_DATA_ATTR: false,
};

const CATEGORY_COLORS = {
  History:      { bg: '#FEF3E2', text: '#8B6914' },
  Politics:     { bg: '#FDEDEC', text: '#C0392B' },
  Economics:    { bg: '#EBF5FB', text: '#2980B9' },
  Culture:      { bg: '#F5EEF8', text: '#8E44AD' },
  Science:      { bg: '#EAFAF1', text: '#27AE60' },
  Architecture: { bg: '#FEF9E7', text: '#D68910' },
  Lectures:     { bg: '#FFF0E8', text: '#E8672E' },
};

function CommentSection({ postId }) {
  const [comments,    setComments]    = useState([]);
  const [loadingC,    setLoadingC]    = useState(true);
  const [submitted,   setSubmitted]   = useState(false);
  const [submitting,  setSubmitting]  = useState(false);
  const [error,       setError]       = useState('');
  const [form,        setForm]        = useState({ name: '', email: '', website: '', content: '' });
  const [hp,          setHp]          = useState('');
  const formLoadTime = useRef(Date.now());

  useEffect(() => {
    if (!postId) return;
    supabase
      .from('comments')
      .select('id, author_name, author_website, content, created_at')
      .eq('post_id', postId)
      .eq('status', 'approved')
      .order('created_at', { ascending: true })
      .then(({ data }) => { setComments(data || []); setLoadingC(false); });
  }, [postId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (hp) return;

    if (Date.now() - formLoadTime.current < 3000) {
      setError('Slow down — please take a moment before submitting.');
      return;
    }

    const urlCount = (form.content.match(/https?:\/\//gi) || []).length;
    if (urlCount > 2) {
      setError('Comments with more than 2 links are not allowed.');
      return;
    }

    if (!form.name.trim() || form.name.trim().length < 2) {
      setError('Please enter your name (at least 2 characters).');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      setError('Please enter a valid email address.');
      return;
    }
    if (form.content.trim().length < 10) {
      setError('Your comment is too short — please write at least 10 characters.');
      return;
    }
    if (form.content.trim().length > 2000) {
      setError('Comment is too long (max 2000 characters).');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/submit-comment`,
        {
          method: 'POST',
          headers: {
            'Content-Type':  'application/json',
            'apikey':        import.meta.env.VITE_SUPABASE_ANON_KEY,
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            post_id:        postId,
            author_name:    form.name.trim(),
            author_email:   form.email.trim().toLowerCase(),
            author_website: form.website.trim() || null,
            content:        form.content.trim(),
          }),
        },
      );
      const result = await res.json();
      if (!res.ok) {
        if (result.error === 'rate_limit_exceeded') {
          setError('You\'ve submitted too many comments recently. Please wait an hour and try again.');
        } else {
          setError('Something went wrong. Please try again.');
        }
      } else {
        setSubmitted(true);
      }
    } catch {
      setError('Could not submit your comment. Check your connection and try again.');
    }
    setSubmitting(false);
  };

  const inputBase = {
    width: '100%', padding: '0.75rem 1rem', fontSize: '0.9rem',
    border: '1.5px solid #E8E4DC', borderRadius: '10px',
    fontFamily: "'Inter', sans-serif", outline: 'none',
    color: '#17140F', background: 'white', boxSizing: 'border-box',
  };

  return (
    <section style={{ borderTop: '2px solid #EDE6D6', paddingTop: '3rem', marginTop: '3rem' }}>
      <h2 style={{ fontSize: '1.25rem', fontWeight: '800', color: '#17140F', margin: '0 0 2rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <MessageSquare size={20} color="#E8672E" />
        {loadingC ? 'Comments' : `${comments.length} Comment${comments.length !== 1 ? 's' : ''}`}
      </h2>

      {/* Approved comments */}
      {!loadingC && comments.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem', marginBottom: '3rem' }}>
          {comments.map(c => (
            <div key={c.id} style={{ display: 'flex', gap: '0.85rem', alignItems: 'flex-start' }}>
              <div style={{
                width: '40px', height: '40px', borderRadius: '50%', flexShrink: 0,
                background: `hsl(${c.author_name.split('').reduce((a, ch) => a + ch.charCodeAt(0), 0) % 360},50%,65%)`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '0.85rem', fontWeight: '700', color: 'white',
              }}>
                {c.author_name.trim().split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.35rem', flexWrap: 'wrap' }}>
                  {c.author_website ? (
                    <a href={c.author_website} target="_blank" rel="noreferrer noopener"
                      style={{ fontWeight: '700', fontSize: '0.92rem', color: '#E8672E', textDecoration: 'none' }}>
                      {c.author_name}
                    </a>
                  ) : (
                    <span style={{ fontWeight: '700', fontSize: '0.92rem', color: '#17140F' }}>{c.author_name}</span>
                  )}
                  <span style={{ fontSize: '0.78rem', color: '#AAAAAA' }}>
                    {new Date(c.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                  </span>
                </div>
                <p style={{ fontSize: '0.92rem', color: '#2A2A2A', lineHeight: 1.7, margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {c.content}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Comment form */}
      {submitted ? (
        <div style={{ background: '#E8F8EF', border: '1px solid #A9DFBF', borderRadius: '12px', padding: '1.5rem', textAlign: 'center' }}>
          <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>✓</div>
          <p style={{ fontWeight: '700', color: '#17140F', margin: '0 0 0.3rem' }}>Comment submitted!</p>
          <p style={{ fontSize: '0.88rem', color: '#4A4A4A', margin: 0 }}>
            Your comment is awaiting moderation and will appear once approved.
          </p>
        </div>
      ) : (
        <div style={{ background: '#FAFAF8', border: '1.5px solid #EDE6D6', borderRadius: '16px', padding: '1.75rem' }}>
          <h3 style={{ fontSize: '1rem', fontWeight: '800', color: '#17140F', margin: '0 0 1.25rem' }}>Leave a Comment</h3>
          <p style={{ fontSize: '0.82rem', color: '#6B665C', margin: '0 0 1.25rem', lineHeight: 1.6 }}>
            All comments are reviewed before appearing. Your email address will not be published.
          </p>

          <form onSubmit={handleSubmit}>
            {/* Honeypot — hidden from real users, bots fill it out */}
            <div style={{ position: 'absolute', left: '-9999px', top: 'auto', width: '1px', height: '1px', overflow: 'hidden' }} aria-hidden="true">
              <label htmlFor="hp_confirm">Leave this empty</label>
              <input id="hp_confirm" name="hp_confirm" type="text" tabIndex="-1" autoComplete="off" value={hp} onChange={e => setHp(e.target.value)} />
            </div>

            <div className="comment-form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
              <div>
                <label style={{ fontSize: '0.78rem', fontWeight: '700', color: '#17140F', display: 'block', marginBottom: '0.35rem' }}>
                  Name <span style={{ color: '#E8672E' }}>*</span>
                </label>
                <input
                  type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="Your name" autoComplete="name" maxLength={80}
                  style={inputBase}
                  onFocus={e => { e.target.style.borderColor = '#E8672E'; }}
                  onBlur={e => { e.target.style.borderColor = '#E8E4DC'; }}
                />
              </div>
              <div>
                <label style={{ fontSize: '0.78rem', fontWeight: '700', color: '#17140F', display: 'block', marginBottom: '0.35rem' }}>
                  Email <span style={{ color: '#E8672E' }}>*</span>
                </label>
                <input
                  type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                  placeholder="your@email.com" autoComplete="email"
                  style={inputBase}
                  onFocus={e => { e.target.style.borderColor = '#E8672E'; }}
                  onBlur={e => { e.target.style.borderColor = '#E8E4DC'; }}
                />
              </div>
            </div>

            <div style={{ marginBottom: '0.75rem' }}>
              <label style={{ fontSize: '0.78rem', fontWeight: '700', color: '#17140F', display: 'block', marginBottom: '0.35rem' }}>
                Website <span style={{ color: '#AAAAAA', fontWeight: '500' }}>(optional)</span>
              </label>
              <input
                type="url" value={form.website} onChange={e => setForm(f => ({ ...f, website: e.target.value }))}
                placeholder="https://yoursite.com" autoComplete="url"
                style={inputBase}
                onFocus={e => { e.target.style.borderColor = '#E8672E'; }}
                onBlur={e => { e.target.style.borderColor = '#E8E4DC'; }}
              />
            </div>

            <div style={{ marginBottom: '1.1rem' }}>
              <label style={{ fontSize: '0.78rem', fontWeight: '700', color: '#17140F', display: 'block', marginBottom: '0.35rem' }}>
                Comment <span style={{ color: '#E8672E' }}>*</span>
              </label>
              <textarea
                value={form.content} onChange={e => setForm(f => ({ ...f, content: e.target.value }))}
                placeholder="Share your thoughts on this lecture…"
                rows={5} maxLength={2000}
                style={{ ...inputBase, resize: 'vertical', lineHeight: '1.65' }}
                onFocus={e => { e.target.style.borderColor = '#E8672E'; }}
                onBlur={e => { e.target.style.borderColor = '#E8E4DC'; }}
              />
              <span style={{ fontSize: '0.73rem', color: '#AAAAAA' }}>{form.content.length}/2000</span>
            </div>

            {error && (
              <div style={{ background: '#FDEDEC', border: '1px solid #F5B7B1', borderRadius: '9px', padding: '0.75rem 1rem', marginBottom: '1rem', fontSize: '0.85rem', color: '#C0392B', fontWeight: '600' }}>
                {error}
              </div>
            )}

            <button type="submit" disabled={submitting}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.5rem',
                background: submitting ? '#F0A07A' : '#E8672E', color: 'white',
                border: 'none', borderRadius: '10px', padding: '0.8rem 1.75rem',
                fontSize: '0.9rem', fontWeight: '700', cursor: submitting ? 'not-allowed' : 'pointer',
                fontFamily: "'Inter', sans-serif", boxShadow: submitting ? 'none' : '0 4px 14px rgba(232, 103, 46,0.3)',
              }}>
              {submitting ? (
                <><div style={{ width: '14px', height: '14px', border: '2px solid rgba(255,255,255,0.4)', borderTopColor: 'white', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} /> Submitting…</>
              ) : (
                <><Send size={15} /> Post Comment</>
              )}
            </button>
          </form>
        </div>
      )}
    </section>
  );
}

export default function PostDetail() {
  const { slug } = useParams();
  const navigate  = useNavigate();
  const heroRef   = useRef(null);
  const [parallax, setParallax] = useState(0);
  const [post, setPost]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [pdfOpen, setPdfOpen]   = useState(false);
  const [videoPlaying, setVideoPlaying] = useState(false);

  useEffect(() => {
    supabase
      .from('posts')
      .select('*')
      .eq('slug', slug)
      .eq('status', 'published')
      .single()
      .then(({ data, error }) => {
        if (error || !data) setNotFound(true);
        else setPost(data);
        setLoading(false);
      });
  }, [slug]);

  useEffect(() => {
    const handleScroll = () => {
      if (heroRef.current) {
        const rect = heroRef.current.getBoundingClientRect();
        setParallax(rect.top * 0.3);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#FFFFFF', fontFamily: "'Inter', sans-serif" }}>
      <div style={{ textAlign: 'center', color: '#6B665C' }}>
        <div style={{ width: '40px', height: '40px', border: '3px solid #EDE6D6', borderTopColor: '#E8672E', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 1rem' }} />
        Loading…
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  if (notFound) return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#FFFFFF', fontFamily: "'Inter', sans-serif", padding: '2rem', textAlign: 'center' }}>
      <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📭</div>
      <h1 style={{ fontSize: '1.5rem', fontWeight: '800', color: '#17140F', margin: '0 0 0.5rem' }}>Post not found</h1>
      <p style={{ color: '#6B665C', margin: '0 0 1.5rem' }}>This lecture may have been removed or the link is incorrect.</p>
      <button onClick={() => navigate('/lectures')} style={{ background: '#E8672E', color: 'white', border: 'none', borderRadius: '10px', padding: '0.75rem 1.5rem', fontWeight: '700', cursor: 'pointer', fontFamily: "'Inter', sans-serif" }}>
        ← Back to Lectures
      </button>
    </div>
  );

  const colors = CATEGORY_COLORS[post.category] || CATEGORY_COLORS.Lectures;
  const youtubeId = getYouTubeId(post.youtube_url);

  return (
    <div style={{ fontFamily: "'Inter', sans-serif", background: '#FFFFFF', minHeight: '100vh' }}>
      <Seo
        title={post.title}
        description={post.meta_description || textSnippet(post.content)}
        image={post.poster_url}
        path={`/lectures/${slug}`}
      />
      <Navbar />

      {/* ── Hero — Contact Us style: full-width centered ── */}
      <section
        ref={heroRef}
        style={{
          paddingTop: '72px',
          position: 'relative',
          minHeight: '520px',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#1A0A02',
        }}
      >
        {post.poster_url ? (
          <img
            src={post.poster_url}
            alt={post.poster_alt || post.title}
            style={{
              position: 'absolute',
              top: '-30%',
              left: 0,
              right: 0,
              width: '100%',
              height: '160%',
              objectFit: 'cover',
              objectPosition: 'center center',
              transform: `translateY(${parallax}px)`,
              willChange: 'transform',
            }}
          />
        ) : (
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, #2A1F0E 0%, #17140F 100%)' }} />
        )}

        {/* Warm overlay */}
        <div style={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(160deg, rgba(180,70,10,0.72) 0%, rgba(120,45,5,0.65) 40%, rgba(30,15,5,0.55) 100%)',
        }} />

        {/* Back button */}
        <button
          onClick={() => navigate('/lectures')}
          style={{
            position: 'absolute', top: '90px', left: '1.5rem',
            display: 'flex', alignItems: 'center', gap: '0.4rem',
            background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.25)',
            borderRadius: '50px', padding: '0.45rem 1rem', color: 'white',
            fontSize: '0.82rem', fontWeight: '600', cursor: 'pointer',
            fontFamily: "'Inter', sans-serif", backdropFilter: 'blur(8px)',
            zIndex: 2,
          }}
        >
          <ArrowLeft size={14} /> All Lectures
        </button>

        {/* Centered content */}
        <div style={{ position: 'relative', zIndex: 1, textAlign: 'center', padding: '5rem 1.5rem 3rem', maxWidth: '860px' }}>
          {/* Category + meta row */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
            <span style={{
              background: colors.bg, color: colors.text,
              fontSize: '0.72rem', fontWeight: '800', letterSpacing: '0.07em',
              textTransform: 'uppercase', padding: '0.3rem 0.85rem', borderRadius: '50px',
            }}>
              {post.category}
            </span>
            <span style={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.82rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
              <Clock size={13} /> {post.reading_time} min read
            </span>
            {post.published_at && (
              <span style={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.82rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                <Calendar size={13} />
                {new Date(post.published_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
              </span>
            )}
          </div>

          <h1 style={{
            fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Helvetica Neue', Arial, sans-serif",
            fontSize: 'clamp(1.75rem, 4.5vw, 3rem)', fontWeight: '600', color: 'white',
            margin: '0 0 1rem', lineHeight: 1.15, letterSpacing: '-0.02em',
          }}>
            {post.title}
          </h1>

          {post.author && (
            <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.92rem', margin: 0 }}>
              By {post.author}
            </p>
          )}
        </div>
      </section>

      {/* ── Watch + Notes banner — top of content ── */}
      <div style={{ background: '#FFF8F4', borderBottom: '1px solid #EDE6D6' }}>
        <div
          className="watch-notes-grid"
          style={{
            maxWidth: '780px', margin: '0 auto', padding: '1rem 1.5rem',
            display: 'grid',
            gridTemplateColumns: youtubeId ? '1.3fr 1fr' : '1fr',
            gap: '1rem', alignItems: 'stretch',
          }}
        >
          {/* Watch on YouTube */}
          {youtubeId && (
            <div style={{
              position: 'relative', borderRadius: '12px', overflow: 'hidden',
              background: '#000', aspectRatio: '16 / 9', boxShadow: '0 2px 12px rgba(0,0,0,0.12)',
            }}>
              {videoPlaying ? (
                <iframe
                  src={`https://www.youtube.com/embed/${youtubeId}?autoplay=1`}
                  title={post.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  style={{ width: '100%', height: '100%', border: 0, display: 'block' }}
                />
              ) : (
                <button
                  onClick={() => setVideoPlaying(true)}
                  aria-label={`Play video: ${post.title}`}
                  style={{
                    position: 'absolute', inset: 0, width: '100%', height: '100%',
                    padding: 0, border: 'none', cursor: 'pointer', background: 'none',
                  }}
                >
                  <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, #2A1F0E 0%, #17140F 100%)' }} />
                  <img
                    loading="lazy"
                    decoding="async"
                    src={`https://i.ytimg.com/vi/${youtubeId}/hqdefault.jpg`}
                    alt=""
                    style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    onError={e => { e.currentTarget.style.display = 'none'; }}
                  />
                  <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.25)' }} />
                  <div style={{
                    position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                    width: '56px', height: '56px', borderRadius: '50%',
                    background: 'rgba(232, 103, 46,0.92)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.35)',
                  }}>
                    <Play size={22} color="white" fill="white" style={{ marginLeft: '3px' }} />
                  </div>
                </button>
              )}
            </div>
          )}

          {/* Read / Download Notes */}
          {post.pdf_url ? (
            <button
              onClick={() => setPdfOpen(true)}
              style={{
                display: 'flex', alignItems: youtubeId ? 'flex-start' : 'center',
                flexDirection: youtubeId ? 'column' : 'row',
                justifyContent: youtubeId ? 'center' : 'space-between',
                gap: youtubeId ? '0.85rem' : 0,
                background: 'white', border: '1.5px solid #FFD4C2', borderRadius: '12px',
                padding: '1.1rem 1.25rem',
                transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                boxShadow: '0 2px 12px rgba(232, 103, 46,0.08)',
                width: '100%', height: '100%', boxSizing: 'border-box',
                cursor: 'pointer', fontFamily: "'Inter', sans-serif",
                textAlign: 'left',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.borderColor = '#E8672E';
                e.currentTarget.style.boxShadow = '0 4px 20px rgba(232, 103, 46,0.15)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.borderColor = '#FFD4C2';
                e.currentTarget.style.boxShadow = '0 2px 12px rgba(232, 103, 46,0.08)';
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{ width: '44px', height: '44px', background: '#FFF0E8', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <BookOpen size={20} color="#E8672E" />
                </div>
                <div>
                  <div style={{ fontSize: '0.95rem', fontWeight: '700', color: '#17140F' }}>Read Lecture Notes</div>
                  <div style={{ fontSize: '0.82rem', color: '#6B665C' }}>View the full PDF notes for this lecture</div>
                </div>
              </div>
              <span style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem',
                background: '#E8672E', color: 'white',
                fontSize: '0.85rem', fontWeight: '700',
                padding: '0.55rem 1.1rem', borderRadius: '8px',
                whiteSpace: 'nowrap', flexShrink: 0,
                width: youtubeId ? '100%' : 'auto',
              }}>
                <BookOpen size={14} /> Read Now
              </span>
            </button>
          ) : (
            <div style={{
              display: 'flex', alignItems: youtubeId ? 'flex-start' : 'center',
              flexDirection: youtubeId ? 'column' : 'row',
              justifyContent: youtubeId ? 'center' : 'space-between',
              gap: youtubeId ? '0.85rem' : 0,
              background: 'white', border: '1.5px solid #EDE6D6', borderRadius: '12px',
              padding: '1.1rem 1.25rem', height: '100%', boxSizing: 'border-box',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{ width: '44px', height: '44px', background: '#F7F5F0', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Download size={20} color="#BBBBBB" />
                </div>
                <div>
                  <div style={{ fontSize: '0.95rem', fontWeight: '700', color: '#17140F' }}>Download Lecture Notes</div>
                  <div style={{ fontSize: '0.82rem', color: '#AAAAAA' }}>PDF notes coming soon for this lecture</div>
                </div>
              </div>
              <span style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem',
                background: '#EDE6D6', color: '#AAAAAA',
                fontSize: '0.85rem', fontWeight: '700',
                padding: '0.55rem 1.1rem', borderRadius: '8px',
                whiteSpace: 'nowrap', flexShrink: 0,
                width: youtubeId ? '100%' : 'auto',
              }}>
                <Download size={14} /> Coming Soon
              </span>
            </div>
          )}
        </div>
      </div>

      {/* ── Body ── */}
      <article style={{ maxWidth: '780px', margin: '0 auto', padding: '3rem 1.5rem 5rem' }}>

        {/* Tags */}
        {post.tags?.length > 0 && (
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '2.5rem' }}>
            {post.tags.map(tag => (
              <span key={tag} style={{ background: '#EDE6D6', color: '#6B665C', fontSize: '0.78rem', fontWeight: '600', padding: '0.3rem 0.75rem', borderRadius: '50px' }}>
                #{tag}
              </span>
            ))}
          </div>
        )}

        {/* Rich text content */}
        <div
          className="lecture-content"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.content || '<p>Content coming soon.</p>', PURIFY_CONFIG) }}
        />

        {/* Bottom download strip */}
        <div style={{ background: '#FFF8F4', border: `1.5px solid ${post.pdf_url ? '#FFD4C2' : '#EDE6D6'}`, borderRadius: '12px', padding: '1.5rem', margin: '3rem 0', textAlign: 'center' }}>
          <p style={{ margin: '0 0 1rem', fontSize: '0.95rem', color: '#4A3A2E', fontWeight: '600' }}>
            Want the full notes to revisit this lecture?
          </p>
          {post.pdf_url ? (
            <button
              onClick={() => setPdfOpen(true)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
                background: '#E8672E', color: 'white', border: 'none',
                padding: '0.75rem 1.75rem', borderRadius: '10px',
                fontSize: '0.9rem', fontWeight: '700', cursor: 'pointer',
                fontFamily: "'Inter', sans-serif",
                boxShadow: '0 4px 16px rgba(232, 103, 46,0.3)',
              }}
            >
              <BookOpen size={16} /> Read Lecture Notes
            </button>
          ) : (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
              background: '#EDE6D6', color: '#AAAAAA',
              padding: '0.75rem 1.75rem', borderRadius: '10px',
              fontSize: '0.9rem', fontWeight: '700',
            }}>
              <Download size={16} /> PDF Notes Coming Soon
            </span>
          )}
        </div>

        {/* Comments */}
        <CommentSection postId={post.id} />

        {/* Back link */}
        <div style={{ borderTop: '1px solid #EDE6D6', paddingTop: '2.5rem', marginTop: '2rem' }}>
          <button
            onClick={() => navigate('/lectures')}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', background: 'none', border: '1.5px solid #E8E4DC', borderRadius: '10px', padding: '0.75rem 1.5rem', color: '#17140F', fontSize: '0.9rem', fontWeight: '700', cursor: 'pointer', fontFamily: "'Inter', sans-serif" }}
          >
            <ArrowLeft size={16} /> Back to All Lectures
          </button>
        </div>
      </article>

      {pdfOpen && (
        <PdfViewerModal
          url={post.pdf_url}
          title={post.title}
          onClose={() => setPdfOpen(false)}
        />
      )}

      <Footer />

      <style>{`
        * { box-sizing: border-box; } body { margin: 0; padding: 0; }
        @keyframes spin { to { transform: rotate(360deg); } }
        @media (min-width: 900px) { .desktop-nav { display: flex !important; } .mobile-menu-btn { display: none !important; } }
        @media (max-width: 899px) { .desktop-nav { display: none !important; } .mobile-menu-btn { display: flex !important; } }
        a:focus-visible, button:focus-visible { outline: 2px solid #E8672E; outline-offset: 2px; }

        .lecture-content { font-size: 1.05rem; line-height: 1.8; color: #2A2A2A; }
        .lecture-content h1 { font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Helvetica Neue', Arial, sans-serif; font-size: 2.0rem;  font-weight: 600; color: #17140F; margin: 3rem 0 1rem; letter-spacing: -0.02em; line-height: 1.15; }
        .lecture-content h2 { font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Helvetica Neue', Arial, sans-serif; font-size: 1.45rem; font-weight: 600; color: #17140F; margin: 2.5rem 0 0.75rem; letter-spacing: -0.02em; }
        .lecture-content h3 { font-size: 1.15rem; font-weight: 700; color: #17140F; margin: 2rem 0 0.6rem; }
        .lecture-content h4 { font-size: 1.05rem; font-weight: 700; color: #17140F; margin: 1.75rem 0 0.5rem; }
        .lecture-content h5 { font-size: 0.95rem; font-weight: 700; color: #3A3A3A; margin: 1.5rem 0 0.4rem; }
        .lecture-content h6 { font-size: 0.88rem; font-weight: 700; color: #6B665C; margin: 1.25rem 0 0.35rem; }
        .lecture-content p  { margin: 0 0 1.25rem; }
        .lecture-content ul, .lecture-content ol { padding-left: 1.5rem; margin: 0 0 1.25rem; }
        .lecture-content li { margin-bottom: 0.4rem; }
        .lecture-content blockquote {
          border-left: 4px solid #E8672E; margin: 2rem 0;
          padding: 1rem 1.5rem; background: #FFF8F4; border-radius: 0 10px 10px 0;
          color: #4A3A2E; font-style: italic; font-size: 1.05rem;
        }
        .lecture-content strong { color: #17140F; font-weight: 700; }
        .lecture-content a { color: #E8672E; text-decoration: underline; }
        .lecture-content img { max-width: 100%; height: auto; display: block; border-radius: 8px; margin: 1.25rem auto; }
        .lecture-content mark { border-radius: 3px; padding: 0 2px; }
        @media (max-width: 600px) {
          .comment-form-grid { grid-template-columns: 1fr !important; }
          .lecture-content h2 { font-size: 1.2rem !important; }
          .lecture-content blockquote { padding: 0.75rem 1rem !important; }
        }
        @media (max-width: 680px) {
          .watch-notes-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}
